const ver = "1.4.0";
console.warn("UNIFY-THREE Version " + ver);

import * as THREE from "three";
import { UnifyEngine } from "./UnifyEngine";
import { Input } from "./Input";
import { Utilities } from "./Utilities";
import { AssetLoader } from "./AssetLoader";
import { Create } from "./Primitives";
import { ObjectTools } from "./ObjectTools";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass";
import { EditControl } from "./EditControl";

/**
 * This is the core Unify-Three module.
 *
 * @param {*} config { debugMode: bool, editMode: bool, postProcessorEnabled: bool }
 */
export function Unify_Three(config) {
  config = Object.assign(
    {
      editMode: false,
      debugMode: false,
      postProcessorEnabled: true
    },
    config
  );

  // -----------------------------------------------------------------------------------
  // Scene & Camera Setup
  // -----------------------------------------------------------------------------------
  this.scene = new THREE.Scene();
  this.camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 500);

  // -----------------------------------------------------------------------------------
  // Renderer
  // -----------------------------------------------------------------------------------
  this.renderer = new THREE.WebGLRenderer({ antialias: true });
  this.canvas = this.renderer.domElement;
  this.renderer.setSize(window.innerWidth, window.innerHeight);
  document.body.appendChild(this.canvas);

  // -----------------------------------------------------------------------------------
  // Assets
  // -----------------------------------------------------------------------------------
  this.assets = {};

  //this.sceneObjects = {};

  this.addNewAsset = (asset, name) => {
    if (typeof asset == "string") {
      let temp = name;
      name = asset; // swap arugments for backwards compatibility
      asset = temp;
    }
    if (typeof name == "string") {
      if (Object.keys(this.assets).includes(name)) {
        let newName = this.utilities.incrementString(name);
        this.addNewAsset(asset, newName);
      } else {
        this.assets[name] = asset;
      }
    }
  };

  //this.addNewAnimation = (anim, name) => {};
  //this.addNewSceneObject = (object, name) => {};

  // depreciating
  this.getAssets = () => {
    return this.assets;
  };

  // -----------------------------------------------------------------------------------
  // Modules
  // -----------------------------------------------------------------------------------
  this.modules = {};
  let legacyFlag = true;

  /**
   * Registers a Unify module's callback functions
   *
   * @param {string} customModule
   * @param {string} moduleName reference added to main.modules.[moduleName]
   */
  this.addModule = (customModule, moduleName) => {
    let newModule;
    // type check ---------------------------------
    if (!customModule.hasOwnProperty("Unify")) {
      // this is a LEGACY module
      newModule = customModule;
    } else {
      // this is a NEW UNIFY module
      newModule = new customModule.Unify(this);
      legacyFlag = false;
    }

    // name check ----------------------------------
    if (newModule.hasOwnProperty("unify_id")) moduleName = newModule.unify_id;
    else {
      if (moduleName == null) {
        moduleName = "_<unnamed>_";
      }
    }
    // check name for dupes
    if (Object.keys(this.modules).includes(moduleName)) {
      // duplicate name
      console.warn("Unify: Duplicate ");
      let newName = this.utilities.incrementString(moduleName);
      this.addModule(newModule, newName);
    }

    // add module to engine ------------------------
    this.engine.addModule(newModule, moduleName, true);
    if (legacyFlag) console.warn(moduleName + " registered using legacy method. Please update.");
    // update local modules cache
    this.modules = this.getModules();
    legacyFlag = true;
  };

  /** @function getModules - */
  this.getModules = () => {
    return this.engine.getModules();
  };

  // Internal Modules
  this.engine = new UnifyEngine();
  this.input = new Input(this);
  this.utilities = new Utilities(this);
  this.create = Create;
  this.objectTools = new ObjectTools(this);
  this.assetLoader = new AssetLoader(this);
  this.engine.addModule(this.assetLoader, "unify_internal", true);

  this.engine.addAwake(() => {
    this.modules = this.engine.getModules();
  });
  // -----------------------------------------------------------------------------------
  // Post Processor
  // -----------------------------------------------------------------------------------
  const composer = new EffectComposer(this.renderer);
  const renderPass = new RenderPass(this.scene, this.camera);
  composer.addPass(renderPass);

  // -----------------------------------------------------------------------------------
  // RENDER
  // -----------------------------------------------------------------------------------
  this.render = () => {
    if (config.postProcessorEnabled) {
      composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  };

  this.engine.setRenderCallback(this.render);
  // -----------------------------------------------------------------------------------
  // Edit / Debug
  // -----------------------------------------------------------------------------------
  this.editMode = config.editMode;
  this.debugMode = config.debugMode;
  this.engine.setDebugMode(this.debugMode);
  // edit mode
  if (this.editMode) {
    const editControls = new EditControl(this);
    this.engine.addModule(editControls, "unify_internal", true);
  }
  // debug mode
  if (this.debugMode) {
    this.engine.addUpdate(fpsCalc);
  }
  // calc frames per second
  let fps;
  let frames = [];
  function fpsCalc(deltaTime) {
    frames.push(deltaTime);
    if (frames.length == 10) {
      fps = 1 / (frames.reduce((a, b) => a + b, 0) / 10);
      fps = fps.toFixed(0);
      frames = [];
    }
  }
  this.getFps = () => {
    return fps;
  };
  // -----------------------------------------------------------------------------------
  // Interface Functions
  // -----------------------------------------------------------------------------------

  /** @function addRenderPass - Adds renderPass to Post Processing stack */
  this.addRenderPass = renderPass => {
    composer.addPass(renderPass);
  };

  /** @function getObjectsByName - Traverses entire scene and returns array of objects with .name matching "name" */
  this.getObjectsByName = name => {
    let foundObjects = [];
    this.scene.traverse(object => {
      if (object.name == name) foundObjects.push(object);
    });
    return foundObjects;
  };

  /** @function getDeltaTime - */
  this.getDeltaTime = () => {
    return this.engine.getDeltaTime();
  };

  /** @function getTime - */
  this.getTime = () => {
    return this.engine.getTime();
  };

  /** @function awake - */
  this.awake = (autoStart, startCallback) => {
    this.engine.awake(autoStart, startCallback);
  };

  /** @function start - */
  this.start = startCallback => {
    this.engine.start(startCallback);
    if (this.debugMode) console.log("[SCENE contains (next line) -> ]");
    if (this.debugMode) console.log(this.scene.children);
  };

  /**
   * Adds to main.scene and returns a "deep clone" of a model from main.assets.models.
   * Deep clone will include any bones and skinned meshes, as well as an AnimationMixer ([clone].userData.mixer)
   * and an AnimationAction for all available animation clips ([clone].userData.animations.[clipName])
   *
   * @param {string} modelName
   * @param {boolean} cloneMaterials
   * @returns {object}
   */
  this.instantiate = (modelName, cloneMaterials) => {
    if (typeof this.assets.models[modelName] != null) {
      // get "deep clone" including bones,
      let clone = this.objectTools.deepClone(this.assets.models[modelName], cloneMaterials);
      clone.name = modelName;
      this.scene.add(clone);
      return clone;
    } else {
      console.warn("AssetLoader: " + modelName + " not found in main.assets, returning an empty object");
      return new THREE.Object3D();
    }
  };

  /** @function debugBox - */
  this.debugBox = position => {
    let box = this.create.cube(this);
    box.position.copy(position);
    box.scale.set(0.2, 0.2, 0.2);
    return box;
  };

  // Update Viewport on Resize
  window.addEventListener("resize", () => {
    let width = window.innerWidth;
    let height = window.innerHeight;
    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  });

  /* Animator helper
  this.animator = duration => {
    let elapsedTime = 0;

    // update
    this.update = deltaTime => {
      elapsedTime += deltaTime;
      console.log(elapsedTime / duration);
      if (elapsedTime >= duration) {
        this.engine.removeAnimator(this.update);
      }
    };
    this.engine.addAnimator(this.update);
    // start
    this.start = () => {};
  };*/
  // const scene = main.scene;
}
